/**
 * Maps a gbusiness backend error to a localized message for the user.
 *
 * Backend responses have shape `{ detail: "...", code: "stable_code" }` (added by
 * `gbusiness/exception_handler.py`). We use `code` to look up an i18n key under
 * `gbusiness.errors.<code>` and fall back to `gbusiness.errors.generic` if the
 * code is unknown — `detail` is in English and is meant for logs, not users.
 *
 * Field errors (§182): when the backend — its own post validation, or a Google
 * rejection it could pin to a field — knows WHICH field is wrong, the body also
 * carries `field`, `rule` and `params`. `rule` is the same code the composer's
 * live validation uses (`gbusiness.publications.validation.<rule>`), so the user
 * reads the same sentence wherever the problem was caught. `gbusinessFieldError`
 * returns `{ field, error }` so the page can mark that input in red.
 *
 * `google_api_error` without a field keeps Google's reason (the only thing the
 * user can act on, e.g. "unsupported aspect ratio"), but inside a translated
 * sentence instead of bare English.
 *
 * Usage:
 *     import { gbusinessErrorMessage } from '@/api/gbusinessErrors';
 *     toast.error(gbusinessErrorMessage(error, t));
 *     // or with a context-specific fallback:
 *     toast.error(gbusinessErrorMessage(error, t, t('gbusiness.publications.publishError')));
 */
export function gbusinessErrorMessage(error, t, fallback) {
  const data = error?.response?.data;
  const code = data?.code;
  const detail = data?.detail;

  if (data?.rule) {
    const key = `gbusiness.publications.validation.${data.rule}`;
    const translated = t(key, data.params || {});
    if (translated !== key) return translated;
  }

  if (code === 'google_api_error' && typeof detail === 'string' && detail.trim()) {
    return t('gbusiness.errors.google_rejected', { reason: detail.replace(/^Google:\s*/, '') });
  }

  // Backend already formats the user-friendly message with location name +
  // address for unverified/suspended locations (see UnverifiedLocationError):
  // "No podemos trabajar con el local X - Y porque no está verificado con
  // Google o tiene otros problemas con Google."
  if (code === 'location_unverified_or_invalid' && typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (code) {
    const key = `gbusiness.errors.${code}`;
    const translated = t(key);
    if (translated !== key) return translated;
  }
  return fallback || t('gbusiness.errors.generic');
}

/**
 * The composer field an error points at, if the backend said which one:
 * `{ field: 'eventTitle', error: { code: 'title_too_long', params } }`, or null.
 */
export function gbusinessFieldError(error) {
  const data = error?.response?.data;
  if (!data?.field || !data?.rule) return null;
  return { field: data.field, error: { code: data.rule, params: data.params || {} } };
}
